import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import ConvertedUnits from 'src/components/ConvertedUnits';
import { convertToUserUnits, performReverseConversion } from 'src/utils/conversions';
import { getUuid } from 'src/utils/url';
import { validateMultipleZeros } from 'src/utils/validation';

const ContainerRow = ({
  container,
  units,
  amount,
  onAmountChange,
  initialAmount,
  error,
  isScanned,
}) => {
  // Convert container quantities to user's preferred units
  const {
    quantity: convertedContainerQuantity,
    units: convertedUnits,
    isConverted,
  } = useMemo(() => convertToUserUnits(container.quantity, units), [container.quantity, units]);

  const { quantity: convertedInitialAmount } = useMemo(
    () => convertToUserUnits(initialAmount || 0, units),
    [initialAmount, units]
  );

  // Convert the current amount (from parent state in base units) to converted units for display
  const { quantity: convertedAmount } = useMemo(
    () => convertToUserUnits(amount || 0, units),
    [amount, units]
  );

  const handleInputChange = (e) => {
    let value = e.target.value;

    // Validate multiple zeros
    validateMultipleZeros(e);

    // Allow empty or valid decimal numbers with up to 3 decimal places
    const regex = /^\d*\.?\d{0,3}$/;

    if (value === '') {
      onAmountChange(container.uri, 0, '');
      return;
    }

    if (regex.test(value)) {
      const numericValueConverted = parseFloat(value);

      // Calculate max allowed in converted units
      const maxAllowedConverted = convertedContainerQuantity + convertedInitialAmount;

      // Prevent typing more than max allowed quantity
      if (numericValueConverted > maxAllowedConverted) {
        // Don't update, prevent the input
        e.preventDefault();
        return;
      }

      // Convert the input value back to base units before updating parent state
      if (isConverted) {
        const { quantity: baseQuantity } = performReverseConversion(
          numericValueConverted,
          convertedUnits,
          units
        );
        onAmountChange(container.uri, baseQuantity, '');
      } else {
        // No conversion needed, use value as-is
        onAmountChange(container.uri, numericValueConverted, '');
      }
    }
  };

  const containerUuid = getUuid(container.uri);
  // Get short UUID - first 8 characters
  const shortUuid = containerUuid.substring(0, 8);

  return (
    <Row
      className={`align-items-center py-3 ${isScanned ? 'border-start border-primary' : ''}`}
      style={isScanned ? { backgroundColor: 'rgb(227 242 253)' } : {}}
    >
      <Col xs={4}>
        <div className="inventory-container-uuid">
          {shortUuid}
        </div>
      </Col>
      <Col xs={4}>
        <div className="inventory-quantity-value">
          <ConvertedUnits skipTooltip displayZero quantity={container.quantity} units={units} />
        </div>
      </Col>
      <Col xs={4}>
        <input
          type="number"
          value={convertedAmount !== undefined && convertedAmount !== null ? convertedAmount : ''}
          className={`form-control ${error ? 'is-invalid' : ''} ${amount > 0 ? 'input-has-amount' : ''}`}
          placeholder="0"
          min="0"
          max={convertedContainerQuantity + convertedInitialAmount}
          step="0.001"
          onChange={handleInputChange}
        />
        {error && (
          <div className="invalid-feedback d-block">
            {error}
          </div>
        )}
      </Col>
    </Row>
  );
};

ContainerRow.propTypes = {
  container: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
  }).isRequired,
  units: PropTypes.string.isRequired,
  amount: PropTypes.number,
  onAmountChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  isScanned: PropTypes.bool,
  initialAmount: PropTypes.number,
};

ContainerRow.defaultProps = {
  amount: 0,
  error: '',
  isScanned: false,
  initialAmount: 0,
};

export default ContainerRow;

